<script setup lang="ts">
import { usePWA } from '@/composables/usePWA'
import { useThemeStore } from '@/stores/theme'
import { Download, RefreshCw, WifiOff, X } from 'lucide-vue-next'
import Button from './Button.vue'

const pwa = usePWA()
const themeStore = useThemeStore()

const dismissUpdate = () => {
  pwa.state.value.updateAvailable = false
}
</script>

<template>
  <!-- Banner de Instalação -->
  <Transition
    enter-active-class="transition duration-300 ease-out"
    enter-from-class="opacity-0 translate-y-full"
    enter-to-class="opacity-100 translate-y-0"
    leave-active-class="transition duration-200 ease-in"
    leave-from-class="opacity-100 translate-y-0"
    leave-to-class="opacity-0 translate-y-full"
  >
    <div
      v-if="pwa.state.canInstall && !pwa.state.isInstalled"
      :class="[
        'fixed bottom-4 left-4 right-4 md:left-auto md:right-4 md:w-96 z-50 p-4 rounded-xl shadow-2xl',
        themeStore.isGlassmorphism
          ? 'glass-card'
          : 'bg-card border'
      ]"
    >
      <div class="flex items-start gap-3">
        <div class="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
          <Download class="w-5 h-5 text-primary" />
        </div>
        <div class="flex-1">
          <h3 class="font-semibold">Instalar Kardapio</h3>
          <p class="text-sm text-muted-foreground mt-1">
            Instale o app para acesso rápido e trabalho offline
          </p>
          <div class="flex gap-2 mt-3">
            <Button size="sm" @click="pwa.install">
              Instalar
            </Button>
            <Button variant="ghost" size="sm" @click="pwa.state.canInstall = false">
              Depois
            </Button>
          </div>
        </div>
      </div>
    </div>
  </Transition>

  <!-- Banner de Atualização -->
  <Transition
    enter-active-class="transition duration-300 ease-out"
    enter-from-class="opacity-0 -translate-y-full"
    enter-to-class="opacity-100 translate-y-0"
    leave-active-class="transition duration-200 ease-in"
    leave-from-class="opacity-100 translate-y-0"
    leave-to-class="opacity-0 -translate-y-full"
  >
    <div
      v-if="pwa.state.updateAvailable"
      class="fixed top-4 left-1/2 -translate-x-1/2 z-50 px-4 py-3 bg-primary text-primary-foreground rounded-lg shadow-lg flex items-center gap-3"
    >
      <RefreshCw class="w-4 h-4 animate-spin" />
      <span class="text-sm font-medium">Nova versão disponível</span>
      <Button 
        variant="secondary" 
        size="sm" 
        class="h-7 ml-2"
        @click="pwa.update"
      >
        Atualizar
      </Button>
      <button @click="dismissUpdate" class="ml-2 hover:opacity-70">
        <X class="w-4 h-4" />
      </button>
    </div>
  </Transition>

  <!-- Banner Offline -->
  <Transition
    enter-active-class="transition duration-300 ease-out"
    enter-from-class="opacity-0 -translate-y-full"
    enter-to-class="opacity-100 translate-y-0"
    leave-active-class="transition duration-200 ease-in"
    leave-from-class="opacity-100 translate-y-0"
    leave-to-class="opacity-0 -translate-y-full"
  >
    <div
      v-if="pwa.state.isOffline"
      class="fixed top-0 left-0 right-0 z-50 px-4 py-2 bg-yellow-500 text-yellow-950 flex items-center justify-center gap-2"
    >
      <WifiOff class="w-4 h-4" />
      <span class="text-sm font-medium">Você está offline. Algumas funcionalidades podem não estar disponíveis.</span>
    </div>
  </Transition>
</template>
